import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About" },
  { path: "/contact", label: "Contact" },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Outfit:wght@200;300;400;500&display=swap');

        /* ══════════════════════════════════════════
           HDR ROOT
        ══════════════════════════════════════════ */
        .hdr {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 1000;
          transition:
            background 0.6s cubic-bezier(0.4,0,0.2,1),
            box-shadow 0.6s cubic-bezier(0.4,0,0.2,1);
        }

        .hdr.top {
          background: rgba(252, 250, 246, 0.78) !important;
          backdrop-filter: blur(16px) saturate(1.5) !important;
          -webkit-backdrop-filter: blur(16px) saturate(1.5) !important;
          box-shadow: 0 0 0 1px rgba(201,168,76,0.08), 0 2px 20px rgba(10,8,4,0.04) !important;
        }

        .hdr.scrolled {
          background: rgba(252, 250, 246, 0.97) !important;
          backdrop-filter: blur(28px) saturate(2) !important;
          -webkit-backdrop-filter: blur(28px) saturate(2) !important;
          box-shadow: 0 0 0 1px rgba(201,168,76,0.16), 0 4px 40px rgba(10,8,4,0.07) !important;
        }

        /* Gradient gold line at bottom */
        .hdr::after {
          content: '';
          position: absolute;
          bottom: 0; left: 5%; right: 5%;
          height: 1px;
          background: linear-gradient(to right, transparent, rgba(201,168,76,0.4) 30%, rgba(201,168,76,0.4) 70%, transparent);
          opacity: 0;
          transition: opacity 0.6s ease;
        }
        .hdr.scrolled::after { opacity: 1; }

        /* ══════════════════════════════════════════
           INNER BAR
        ══════════════════════════════════════════ */
        .hdr-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 76px;
          padding: 0 5vw;
          max-width: 1440px;
          margin: 0 auto;
        }

        /* ══════════════════════════════════════════
           LOGO
        ══════════════════════════════════════════ */
        .hdr-logo {
          display: flex;
          align-items: center;
          gap: 11px;
          text-decoration: none;
          flex-shrink: 0;
        }
        .hdr-logo img.logo-mark {
          width: 38px; height: 31px;
          object-fit: contain;
          transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1);
          filter: drop-shadow(0 1px 4px rgba(201,168,76,0.18));
        }
        .hdr-logo img.logo-text {
          height: 28px; width: auto;
          object-fit: contain;
          opacity: 0.88;
          transition: opacity 0.3s;
        }
        .hdr-logo:hover img.logo-mark { transform: scale(1.1) rotate(-3deg); }
        .hdr-logo:hover img.logo-text { opacity: 1; }

        /* ══════════════════════════════════════════
           DESKTOP NAV
        ══════════════════════════════════════════ */
        .hdr-nav {
          display: flex;
          align-items: center;
        }

        /* Thin vertical divider */
        .hdr-divider {
          width: 1px;
          height: 20px;
          background: linear-gradient(to bottom, transparent, rgba(201,168,76,0.3), transparent);
          margin-right: 24px;
          flex-shrink: 0;
        }

        .hdr-link {
          position: relative;
          font-family: 'Outfit', sans-serif !important;
          font-size: 10.5px !important;
          font-weight: 400 !important;
          letter-spacing: 3.2px !important;
          text-transform: uppercase !important;
          color: rgba(22, 18, 8, 0.58) !important;
          text-decoration: none !important;
          padding: 10px 18px !important;
          transition: color 0.3s ease !important;
          white-space: nowrap;
          overflow: visible;
        }

        /* Gold sliding underline */
        .hdr-link::after {
          content: '' !important;
          position: absolute !important;
          bottom: 4px !important;
          left: 18px !important; right: 18px !important;
          height: 1px !important;
          background: linear-gradient(to right, #c9a84c, #e0c070) !important;
          transform: scaleX(0) !important;
          transform-origin: left !important;
          transition: transform 0.4s cubic-bezier(0.4,0,0.2,1) !important;
          display: block !important;
        }

        /* Small gold dot for active */
        .hdr-link::before {
          content: '' !important;
          position: absolute !important;
          bottom: 2px !important;
          left: 50% !important;
          transform: translateX(-50%) scale(0) !important;
          width: 3px !important; height: 3px !important;
          border-radius: 50% !important;
          background: #c9a84c !important;
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1) !important;
          display: block !important;
        }

        .hdr-link:hover { color: rgba(10, 8, 2, 0.88) !important; }
        .hdr-link.active { color: rgba(10, 8, 2, 0.85) !important; }
        .hdr-link:hover::after,
        .hdr-link.active::after { transform: scaleX(1) !important; }
        .hdr-link.active::before { transform: translateX(-50%) scale(1) !important; }

        /* ── Contact CTA ── */
        .hdr-cta {
          font-family: 'Outfit', sans-serif !important;
          font-size: 10px !important;
          font-weight: 500 !important;
          letter-spacing: 2.8px !important;
          text-transform: uppercase !important;
          color: #120e04 !important;
          background: linear-gradient(135deg, #c9a84c 0%, #dbbf69 50%, #c9a84c 100%) !important;
          background-size: 200% !important;
          text-decoration: none !important;
          padding: 11px 26px !important;
          margin-left: 20px !important;
          display: inline-block !important;
          transition: background-position 0.5s ease, transform 0.25s ease, box-shadow 0.3s ease !important;
          clip-path: polygon(0 0, calc(100% - 9px) 0, 100% 9px, 100% 100%, 9px 100%, 0 calc(100% - 9px)) !important;
          white-space: nowrap !important;
          box-shadow: 0 2px 18px rgba(201,168,76,0.22) !important;
        }
        .hdr-cta::before, .hdr-cta::after { display: none !important; }
        .hdr-cta:hover {
          background-position: right center !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 28px rgba(201,168,76,0.32) !important;
          color: #120e04 !important;
        }

        /* ══════════════════════════════════════════
           HAMBURGER
        ══════════════════════════════════════════ */
        .hdr-burger {
          display: none;
          flex-direction: column;
          justify-content: center;
          align-items: flex-end;
          gap: 6px;
          width: 44px; height: 44px;
          padding: 10px 11px;
          background: transparent;
          border: 1px solid rgba(201,168,76,0.28);
          cursor: pointer;
          clip-path: polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px));
          transition: border-color 0.3s, background 0.3s;
        }
        .hdr-burger:hover {
          border-color: rgba(201,168,76,0.6);
          background: rgba(201,168,76,0.03);
        }
        .hdr-burger span {
          display: block;
          height: 1px;
          background: rgba(20, 16, 8, 0.6);
          border-radius: 1px;
          transition: transform 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.25s, width 0.3s ease;
          transform-origin: center;
        }
        .hdr-burger span:nth-child(1) { width: 22px; }
        .hdr-burger span:nth-child(2) { width: 15px; }
        .hdr-burger span:nth-child(3) { width: 22px; }
        .hdr-burger:hover span { width: 22px !important; }
        .hdr-burger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); width: 22px; }
        .hdr-burger.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
        .hdr-burger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); width: 22px; }

        /* ══════════════════════════════════════════
           OVERLAY
        ══════════════════════════════════════════ */
        .hdr-overlay {
          position: fixed;
          inset: 0;
          background: rgba(4,3,1,0.55);
          backdrop-filter: blur(8px);
          z-index: 1100;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.4s ease;
        }
        .hdr-overlay.open { opacity: 1; pointer-events: all; }

        /* ══════════════════════════════════════════
           DRAWER
        ══════════════════════════════════════════ */
        .hdr-drawer {
          position: fixed;
          top: 0; right: 0;
          width: min(340px, 86vw);
          height: 100%;
          background: #faf8f3;
          border-left: 1px solid rgba(201,168,76,0.14);
          z-index: 1200;
          display: flex;
          flex-direction: column;
          transform: translateX(100%);
          transition: transform 0.48s cubic-bezier(0.4,0,0.2,1);
          overflow: hidden;
          box-shadow: -8px 0 48px rgba(10,8,4,0.12);
        }
        .hdr-drawer.open { transform: translateX(0); }

        .hdr-drawer::before {
          content: '';
          display: block;
          height: 3px;
          background: linear-gradient(to right, #b89238, #e8d08a 50%, #b89238);
          flex-shrink: 0;
        }

        .drawer-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 22px 28px;
          border-bottom: 1px solid rgba(201,168,76,0.1);
        }

        .drawer-close {
          width: 36px; height: 36px;
          background: transparent;
          border: 1px solid rgba(201,168,76,0.28);
          color: rgba(20,16,8,0.45);
          cursor: pointer;
          font-size: 13px;
          display: flex; align-items: center; justify-content: center;
          clip-path: polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px));
          transition: all 0.25s;
          font-family: 'Outfit', sans-serif;
          letter-spacing: 0;
        }
        .drawer-close:hover {
          border-color: #c9a84c;
          color: #8a6f28;
          background: rgba(201,168,76,0.06);
        }

        .drawer-section-label {
          font-family: 'Outfit', sans-serif;
          font-size: 8.5px;
          letter-spacing: 4.5px;
          text-transform: uppercase;
          color: #c9a84c;
          padding: 26px 28px 14px;
          display: block;
          opacity: 0.8;
        }

        .drawer-nav {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 0;
          border-top: 1px solid rgba(201,168,76,0.08);
        }

        .drawer-link {
          display: flex;
          align-items: baseline;
          gap: 10px;
          font-family: 'Cormorant Garamond', serif;
          font-size: 33px;
          font-weight: 300;
          font-style: normal;
          color: rgba(22,18,8,0.35);
          text-decoration: none;
          padding: 18px 28px;
          border-bottom: 1px solid rgba(201,168,76,0.07);
          transition: color 0.3s ease, padding-left 0.35s cubic-bezier(0.4,0,0.2,1);
          letter-spacing: 0.3px;
          line-height: 1;
        }
        .drawer-link:last-child { border-bottom: none; }
        .drawer-link:hover { color: rgba(22,18,8,0.82); padding-left: 36px; }
        .drawer-link.active { color: rgba(22,18,8,0.88); font-style: italic; }

        .drawer-link-num {
          font-family: 'Outfit', sans-serif;
          font-size: 9px;
          letter-spacing: 1.5px;
          color: rgba(201,168,76,0.6);
          font-style: normal;
          align-self: flex-start;
          margin-top: 6px;
        }

        .drawer-link-arrow {
          margin-left: auto;
          font-size: 12px;
          color: #c9a84c;
          opacity: 0;
          transform: translateX(-10px);
          transition: opacity 0.3s, transform 0.3s;
          font-style: normal;
        }
        .drawer-link:hover .drawer-link-arrow,
        .drawer-link.active .drawer-link-arrow {
          opacity: 1;
          transform: translateX(0);
        }

        .drawer-foot {
          padding: 24px 28px 30px;
          border-top: 1px solid rgba(201,168,76,0.1);
          background: rgba(201,168,76,0.025);
        }
        .drawer-foot-label {
          font-family: 'Outfit', sans-serif;
          font-size: 8px;
          letter-spacing: 4px;
          text-transform: uppercase;
          color: #c9a84c;
          display: block;
          margin-bottom: 14px;
          opacity: 0.8;
        }
        .drawer-foot-info {
          font-family: 'Outfit', sans-serif;
          font-size: 12px;
          font-weight: 300;
          color: rgba(22,18,8,0.42);
          line-height: 2.3;
          letter-spacing: 0.3px;
        }

        /* ══════════════════════════════════════════
           RESPONSIVE
        ══════════════════════════════════════════ */
        @media (max-width: 900px) {
          .hdr-nav { display: none !important; }
          .hdr-burger { display: flex !important; }
          .hdr-bar { height: 68px; }
        }
      `}</style>

      {/* ── Fixed header ── */}
      <div className={`hdr ${scrolled ? "scrolled" : "top"}`}>
        <div className="hdr-bar">
          {/* Logo */}
          <Link to="/" className="hdr-logo">
            <img
              src="/img/logo.png"
              alt="Gemstone logo"
              className="logo-mark"
            />
            <img src="/img/logoName.png" alt="Gemstone" className="logo-text" />
          </Link>

          {/* Desktop nav */}
          <nav className="hdr-nav">
            <div className="hdr-divider" />
            {navLinks.slice(0, 2).map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                className={`hdr-link ${isActive(path) ? "active" : ""}`}
              >
                {label}
              </Link>
            ))}
            <Link to="/contact" className="hdr-cta">
              Contact Us
            </Link>
          </nav>

          {/* Hamburger */}
          <button
            className={`hdr-burger ${isOpen ? "open" : ""}`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* ── Overlay ── */}
      <div
        className={`hdr-overlay ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(false)}
      />

      {/* ── Drawer ── */}
      <div className={`hdr-drawer ${isOpen ? "open" : ""}`}>
        <div className="drawer-head">
          <Link to="/" className="hdr-logo" onClick={() => setIsOpen(false)}>
            <img
              src="/img/logo.png"
              alt="logo"
              className="logo-mark"
              style={{ width: 30, height: 25 }}
            />
            <img
              src="/img/logoName.png"
              alt="Gemstone"
              className="logo-text"
              style={{ height: 22 }}
            />
          </Link>
          <button className="drawer-close" onClick={() => setIsOpen(false)}>
            ✕
          </button>
        </div>

        <span className="drawer-section-label">Navigation</span>

        <nav className="drawer-nav">
          {navLinks.map(({ path, label }, i) => (
            <Link
              key={path}
              to={path}
              className={`drawer-link ${isActive(path) ? "active" : ""}`}
              onClick={() => setIsOpen(false)}
            >
              <span className="drawer-link-num">0{i + 1}</span>
              {label}
              <span className="drawer-link-arrow">→</span>
            </Link>
          ))}
        </nav>

        <div className="drawer-foot">
          <span className="drawer-foot-label">Reach Us</span>
          <div className="drawer-foot-info">
            gemstonereltd@gmail.com
            <br />
            01776-767687
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div style={{ height: 76 }} />
    </>
  );
};

export default Header;
